/**
 * xr_comfort — the ONE model of the player's VR comfort choices (XR P2, 2026-09-28). Pure + a tiny store.
 *
 * Research (Meta locomotion comfort / best practices / user preferences; Road to VR's comfort checklist): offer
 * slide AND teleport movement, snap AND smooth turn, a comfort VIGNETTE while moving or turning, a SEATED mode that
 * pushes the virtual floor down, height taken from the system (never the headset's height alone) with a manual
 * offset, and recentering. Every choice is remembered per player, apart from the desktop graphics settings — a VR
 * choice must never reach the desktop game (`atlas:vr-comfort`, never `atlas:graphics`).
 *
 * The contract between the VR modules: the UI (the VR panel's Comfort page) WRITES through `setComfort`; the
 * locomotion / vignette / vehicle code READS `comfort()` every frame (cheap: a module-level object) and may listen
 * to `onComfortChange`. No module keeps its own copy.
 */

export type MoveMode = 'slide' | 'teleport';
export type TurnMode = 'snap' | 'smooth';
export type Hand = 'left' | 'right';
// Space campaign L (2026-10-01, gap 27): the SECTOR's comfort choices — the headset flies a ship, not a body. The rules
// that read them are pure in lib/space_comfort.ts; the VR Comfort page shows these tiles while you are in the sector.
/** What a headset banks: the AERO LAW's cosmetic coordinated bank rolls the WORLD around the head — off by default. */
export type SpaceBank = 'off' | 'reduced' | 'full';
/** Roll comfort: free (your roll only) · assist (an eased auto-level after an idle moment) · lock (the horizon held). */
export type SpaceRoll = 'free' | 'assist' | 'lock';
/** What closes the vignette in the sector: nothing · turning · turning and thrust (acceleration — never the speed). */
export type SpaceCue = 'off' | 'turns' | 'all';
/** The sector's secondary full-field effects (streaks, dust, the warp tunnel's spin, your own warp flash). */
export type SpaceEffects = 'reduced' | 'full';

export interface XRComfort {
  /** How the move stick moves you: continuous slide, or a validated short hop. */
  move: MoveMode;
  /** How the turn stick turns you. */
  turn: TurnMode;
  /** Snap-turn step, degrees (15 / 30 / 45 are the menu's choices). */
  snapDeg: number;
  /** Smooth-turn rate, degrees per second at full stick. */
  smoothDegPerS: number;
  /** Comfort vignette strength while moving / turning / riding: 0 = off … 1 = strong. */
  vignette: number;
  /** Seated play: the floor is lowered by `seatedOffsetM` so a seated player reaches standing-height content. */
  seated: boolean;
  /** Extra height added in seated mode, metres. */
  seatedOffsetM: number;
  /** A manual height nudge applied in every mode, metres (−0.5 … +0.5). */
  heightOffsetM: number;
  /** The hand that MOVES (the other turns). Left by default (the VR convention). */
  moveHand: Hand;
  /** Riding a vehicle: cap the drawn motion's comfort risk — a stronger vignette and the chase camera off. */
  vehicleComfort: boolean;
  // ── The sector (space campaign L) ──
  spaceBank: SpaceBank;
  spaceRoll: SpaceRoll;
  /** Your roll rate as a fraction of the hull's (0.25 … 1). */
  spaceRollRate: number;
  spaceCue: SpaceCue;
  /** The cockpit REST FRAME (canopy bow, pillars, dash) fixed around your head. */
  spaceFrame: boolean;
  /** The cockpit HUD's size, independent of every other panel (0.75 … 1.5). */
  spaceHudScale: number;
  spaceEffects: SpaceEffects;
}

export const COMFORT_DEFAULTS: Readonly<XRComfort> = Object.freeze({
  move: 'slide',
  turn: 'snap',
  snapDeg: 30,
  smoothDegPerS: 90,
  vignette: 0.6,
  seated: false,
  seatedOffsetM: 0.45,
  heightOffsetM: 0,
  moveHand: 'left',
  vehicleComfort: true,
  spaceBank: 'off',
  spaceRoll: 'assist',
  spaceRollRate: 0.6,
  spaceCue: 'all',
  spaceFrame: true,
  spaceHudScale: 1,
  spaceEffects: 'reduced',
});

const KEY = 'atlas:vr-comfort';
export const SNAP_CHOICES = [15, 30, 45] as const;

/** Clamp and fill one stored / proposed value set into a legal comfort object. Never throws. */
export function sanitizeComfort(raw: unknown): XRComfort {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<Record<keyof XRComfort, unknown>>;
  const num = (v: unknown, d: number, lo: number, hi: number): number => (typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : d);
  const d = COMFORT_DEFAULTS;
  return {
    move: r.move === 'teleport' ? 'teleport' : 'slide',
    turn: r.turn === 'smooth' ? 'smooth' : 'snap',
    snapDeg: num(r.snapDeg, d.snapDeg, 10, 90),
    smoothDegPerS: num(r.smoothDegPerS, d.smoothDegPerS, 30, 240),
    vignette: num(r.vignette, d.vignette, 0, 1),
    seated: r.seated === true,
    seatedOffsetM: num(r.seatedOffsetM, d.seatedOffsetM, 0, 1),
    heightOffsetM: num(r.heightOffsetM, d.heightOffsetM, -0.5, 0.5),
    moveHand: r.moveHand === 'right' ? 'right' : 'left',
    vehicleComfort: r.vehicleComfort !== false,
    spaceBank: r.spaceBank === 'reduced' || r.spaceBank === 'full' ? r.spaceBank : 'off',
    spaceRoll: r.spaceRoll === 'free' || r.spaceRoll === 'lock' ? r.spaceRoll : 'assist',
    spaceRollRate: num(r.spaceRollRate, d.spaceRollRate, 0.25, 1),
    spaceCue: r.spaceCue === 'off' || r.spaceCue === 'turns' ? r.spaceCue : 'all',
    spaceFrame: r.spaceFrame !== false,
    spaceHudScale: num(r.spaceHudScale, d.spaceHudScale, 0.75, 1.5),
    spaceEffects: r.spaceEffects === 'full' ? 'full' : 'reduced',
  };
}

/** The floor offset the rig applies (metres, added to the camera's local-floor height). */
export function floorOffsetM(c: XRComfort): number {
  return (c.seated ? c.seatedOffsetM : 0) + c.heightOffsetM;
}

function load(): XRComfort {
  try { return sanitizeComfort(JSON.parse(localStorage.getItem(KEY) ?? 'null')); } catch { return sanitizeComfort(null); }
}

let current: XRComfort = typeof localStorage === 'undefined' ? sanitizeComfort(null) : load();
const listeners = new Set<(c: XRComfort) => void>();

/** The live comfort choices (do not mutate — use `setComfort`). */
export function comfort(): XRComfort { return current; }

/** Merge a change, persist it, tell every listener. Returns the new value. */
export function setComfort(patch: Partial<XRComfort>): XRComfort {
  current = sanitizeComfort({ ...current, ...patch });
  try { localStorage.setItem(KEY, JSON.stringify(current)); } catch { /* private mode — the session keeps it */ }
  for (const fn of listeners) { try { fn(current); } catch { /* a listener never breaks the store */ } }
  return current;
}

/** Back to the defaults (the Comfort page's reset). */
export function resetComfort(): XRComfort {
  return setComfort({ ...COMFORT_DEFAULTS });
}

/** Subscribe; returns the unsubscribe. */
export function onComfortChange(fn: (c: XRComfort) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
